import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import type { Portfolio, PortfolioInsert, PortfolioUpdate, PortfolioHolding } from '../types/database';
import type { StockQuote } from '../types/market';
import { TradierService } from './TradierService';
import { CacheService } from './CacheService';

export interface HoldingWithQuote extends PortfolioHolding {
  currentPrice: number;
  currentValue: number;
  totalGainLoss: number;
  totalGainLossPercent: number;
  dayChange: number;
  dayChangePercent: number;
}

export interface PortfolioWithQuotes extends Portfolio {
  holdings: HoldingWithQuote[];
  totalValue: number;
  totalCost: number;
  totalGainLoss: number;
  totalGainLossPercent: number;
  dayChange: number;
  dayChangePercent: number;
}

export interface PortfolioPerformance {
  totalValue: number;
  totalCost: number;
  totalGainLoss: number;
  totalGainLossPercent: number;
  dayChange: number;
  dayChangePercent: number;
  topPerformer: HoldingWithQuote | null;
  worstPerformer: HoldingWithQuote | null;
  diversification: {
    [ticker: string]: {
      percentage: number;
      value: number;
    };
  };
}

export interface OptimisticUpdate<T> {
  id: string;
  data: T;
  timestamp: number;
  pending: boolean;
}

export class PortfolioService {
  private static tradierService: TradierService;
  private static cacheService: CacheService;
  private static optimisticUpdates = new Map<string, OptimisticUpdate<Portfolio>>();

  // Initialize services
  static {
    this.tradierService = new TradierService();
    this.cacheService = new CacheService();
  }

  /**
   * Get all portfolios for a user with real-time calculations
   */
  static async getUserPortfolios(userId: string, includeQuotes = true): Promise<PortfolioWithQuotes[]> {
    const cacheKey = `portfolios:${userId}`;
    
    // Check cache first
    const cached = await this.cacheService.get<Portfolio[]>(cacheKey);
    let portfolios = cached;

    if (!portfolios) {
      // Fetch from database
      const { data, error } = await supabase
        .from('portfolios')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching portfolios', error);
        throw new Error(`Failed to fetch portfolios: ${error.message}`);
      }

      portfolios = data || [];
      
      // Cache the result
      await this.cacheService.set(cacheKey, portfolios, 300); // 5 minutes
    }

    // Apply optimistic updates
    const portfoliosWithOptimistic = this.applyOptimisticUpdates(portfolios);

    if (!includeQuotes) {
      return portfoliosWithOptimistic.map(p => this.createPortfolioWithEmptyQuotes(p));
    }

    // Calculate portfolio values with real-time quotes
    const portfoliosWithQuotes = await Promise.all(
      portfoliosWithOptimistic.map(portfolio => this.calculatePortfolioValue(portfolio))
    );

    return portfoliosWithQuotes;
  }

  /**
   * Get a specific portfolio by ID with quotes and calculations
   */
  static async getPortfolio(portfolioId: string, includeQuotes = true): Promise<PortfolioWithQuotes | null> {
    const cacheKey = `portfolio:${portfolioId}`;
    
    // Check cache first
    let portfolio = await this.cacheService.get<Portfolio>(cacheKey);

    if (!portfolio) {
      const { data, error } = await supabase
        .from('portfolios')
        .select('*')
        .eq('id', portfolioId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Not found
        }
        logger.error('Error fetching portfolio', error);
        throw new Error(`Failed to fetch portfolio: ${error.message}`);
      }

      portfolio = data;
      await this.cacheService.set(cacheKey, portfolio, 300);
    }

    // Apply optimistic updates
    const optimisticUpdate = this.optimisticUpdates.get(portfolioId);
    if (optimisticUpdate && optimisticUpdate.pending) {
      portfolio = { ...portfolio, ...optimisticUpdate.data };
    }

    if (!includeQuotes || !portfolio || !portfolio.holdings.length) {
      return this.createPortfolioWithEmptyQuotes(portfolio!);
    }

    return this.calculatePortfolioValue(portfolio);
  }

  /**
   * Create a new portfolio with optimistic updates
   */
  static async createPortfolio(portfolioData: PortfolioInsert): Promise<Portfolio> {
    // Validate holdings
    if (portfolioData.holdings) {
      this.validateHoldings(portfolioData.holdings);
    }

    // Generate temporary ID for optimistic update
    const tempId = `temp_${Date.now()}`;
    const optimisticPortfolio: Portfolio = {
      id: tempId,
      ...portfolioData,
      holdings: portfolioData.holdings || [],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Apply optimistic update
    this.optimisticUpdates.set(tempId, {
      id: tempId,
      data: optimisticPortfolio,
      timestamp: Date.now(),
      pending: true
    });

    try {
      // Perform actual database operation
      const { data, error } = await supabase
        .from('portfolios')
        .insert(portfolioData)
        .select()
        .single();

      if (error) {
        // Remove optimistic update on error
        this.optimisticUpdates.delete(tempId);
        logger.error('Error creating portfolio', error);
        throw new Error(`Failed to create portfolio: ${error.message}`);
      }

      // Replace optimistic update with real data
      this.optimisticUpdates.delete(tempId);
      
      // Invalidate cache
      await this.cacheService.delete(`portfolios:${portfolioData.user_id}`);

      return data;
    } catch (error) {
      this.optimisticUpdates.delete(tempId);
      throw error;
    }
  }

  /**
   * Update a portfolio with optimistic updates
   */
  static async updatePortfolio(
    portfolioId: string, 
    updates: PortfolioUpdate
  ): Promise<Portfolio> {
    // Validate holdings if provided
    if (updates.holdings) {
      this.validateHoldings(updates.holdings);
    }

    // Get current portfolio for optimistic update
    const current = await this.getPortfolio(portfolioId, false);
    if (!current) {
      throw new Error('Portfolio not found');
    }

    // Apply optimistic update
    const optimisticPortfolio = { ...current, ...updates, updated_at: new Date().toISOString() };
    this.optimisticUpdates.set(portfolioId, {
      id: portfolioId,
      data: optimisticPortfolio,
      timestamp: Date.now(),
      pending: true
    });

    try {
      // Perform actual database operation
      const { data, error } = await supabase
        .from('portfolios')
        .update(updates)
        .eq('id', portfolioId)
        .select()
        .single();

      if (error) {
        // Remove optimistic update on error
        this.optimisticUpdates.delete(portfolioId);
        logger.error('Error updating portfolio', error);
        throw new Error(`Failed to update portfolio: ${error.message}`);
      }

      // Remove optimistic update
      this.optimisticUpdates.delete(portfolioId);
      
      // Invalidate cache
      await this.cacheService.delete(`portfolio:${portfolioId}`);
      await this.cacheService.delete(`portfolios:${data.user_id}`);

      return data;
    } catch (error) {
      this.optimisticUpdates.delete(portfolioId);
      throw error;
    }
  }

  /**
   * Delete a portfolio
   */
  static async deletePortfolio(portfolioId: string): Promise<boolean> {
    // Get portfolio for cache invalidation
    const portfolio = await this.getPortfolio(portfolioId, false);
    if (!portfolio) {
      return false;
    }

    const { error } = await supabase
      .from('portfolios')
      .delete()
      .eq('id', portfolioId);

    if (error) {
      logger.error('Error deleting portfolio', error);
      throw new Error(`Failed to delete portfolio: ${error.message}`);
    }

    // Invalidate cache
    await this.cacheService.delete(`portfolio:${portfolioId}`);
    await this.cacheService.delete(`portfolios:${portfolio.user_id}`);

    return true;
  }

  /**
   * Add a holding to a portfolio
   */
  static async addHolding(portfolioId: string, holding: PortfolioHolding): Promise<Portfolio> {
    this.validateHolding(holding);

    const portfolio = await this.getPortfolio(portfolioId, false);
    if (!portfolio) {
      throw new Error('Portfolio not found');
    }

    // Check if holding already exists for this ticker
    const existingIndex = portfolio.holdings.findIndex(h => h.ticker === holding.ticker);
    
    let updatedHoldings: PortfolioHolding[];
    if (existingIndex >= 0) {
      // Merge with existing holding (average cost)
      const existing = portfolio.holdings[existingIndex];
      const totalShares = existing.shares + holding.shares;
      const totalCost = (existing.shares * existing.purchase_price) + (holding.shares * holding.purchase_price);
      const avgPrice = totalCost / totalShares;

      updatedHoldings = [...portfolio.holdings];
      updatedHoldings[existingIndex] = {
        ...existing,
        shares: totalShares,
        purchase_price: avgPrice,
        purchase_date: holding.purchase_date, // Use latest purchase date
        name: holding.name || existing.name // Keep the name, prefer new one if provided
      };
    } else {
      // Add new holding
      updatedHoldings = [...portfolio.holdings, holding];
    }

    return this.updatePortfolio(portfolioId, { holdings: updatedHoldings });
  }

  /**
   * Update a specific holding in a portfolio
   */
  static async updateHolding(
    portfolioId: string, 
    ticker: string, 
    updates: Partial<PortfolioHolding>
  ): Promise<Portfolio> {
    const portfolio = await this.getPortfolio(portfolioId, false);
    if (!portfolio) {
      throw new Error('Portfolio not found');
    }

    const holdingIndex = portfolio.holdings.findIndex(h => h.ticker === ticker);
    if (holdingIndex === -1) {
      throw new Error('Holding not found');
    }

    const updatedHoldings = [...portfolio.holdings];
    updatedHoldings[holdingIndex] = { ...updatedHoldings[holdingIndex], ...updates };

    // Validate updated holding
    this.validateHolding(updatedHoldings[holdingIndex]);

    return this.updatePortfolio(portfolioId, { holdings: updatedHoldings });
  }

  /**
   * Remove a holding from a portfolio
   */
  static async removeHolding(portfolioId: string, ticker: string): Promise<Portfolio> {
    const portfolio = await this.getPortfolio(portfolioId, false);
    if (!portfolio) {
      throw new Error('Portfolio not found');
    }

    const updatedHoldings = portfolio.holdings.filter(h => h.ticker !== ticker);
    return this.updatePortfolio(portfolioId, { holdings: updatedHoldings });
  }

  /**
   * Get detailed portfolio performance analysis
   */
  static async getPortfolioPerformance(portfolioId: string): Promise<PortfolioPerformance> {
    const portfolio = await this.getPortfolio(portfolioId, true);
    if (!portfolio) {
      throw new Error('Portfolio not found');
    }

    const holdings = portfolio.holdings as HoldingWithQuote[];
    
    let topPerformer: HoldingWithQuote | null = null;
    let worstPerformer: HoldingWithQuote | null = null;
    
    if (holdings.length > 0) {
      topPerformer = holdings.reduce((best, current) => 
        current.totalGainLossPercent > best.totalGainLossPercent ? current : best
      );
      
      worstPerformer = holdings.reduce((worst, current) => 
        current.totalGainLossPercent < worst.totalGainLossPercent ? current : worst
      );
    }

    // Calculate diversification
    const diversification: { [ticker: string]: { percentage: number; value: number } } = {};
    holdings.forEach(holding => {
      const percentage = portfolio.totalValue > 0 ? (holding.currentValue / portfolio.totalValue) * 100 : 0;
      diversification[holding.ticker] = {
        percentage,
        value: holding.currentValue
      };
    });

    return {
      totalValue: portfolio.totalValue,
      totalCost: portfolio.totalCost,
      totalGainLoss: portfolio.totalGainLoss,
      totalGainLossPercent: portfolio.totalGainLossPercent,
      dayChange: portfolio.dayChange,
      dayChangePercent: portfolio.dayChangePercent,
      topPerformer,
      worstPerformer,
      diversification
    };
  }

  /**
   * Calculate portfolio value with real-time quotes
   */
  private static async calculatePortfolioValue(portfolio: Portfolio): Promise<PortfolioWithQuotes> {
    if (!portfolio.holdings.length) {
      return this.createPortfolioWithEmptyQuotes(portfolio);
    }

    // Get unique tickers
    const tickers = [...new Set(portfolio.holdings.map(h => h.ticker))];
    
    // Fetch quotes
    const quotes = await this.tradierService.getQuotes(tickers) || [];
    const quoteMap = new Map(quotes.map(quote => [quote.symbol, quote]));

    // Calculate holding values
    const holdingsWithQuotes: HoldingWithQuote[] = portfolio.holdings.map(holding => {
      const quote = quoteMap.get(holding.ticker);
      const currentPrice = quote?.price || 0;
      const currentValue = holding.shares * currentPrice;
      const totalCost = holding.shares * holding.purchase_price;
      const totalGainLoss = currentValue - totalCost;
      const totalGainLossPercent = totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0;
      const dayChange = quote ? holding.shares * quote.change : 0;
      const dayChangePercent = quote?.change_percent || 0;

      return {
        ...holding,
        currentPrice,
        currentValue,
        totalGainLoss,
        totalGainLossPercent,
        dayChange,
        dayChangePercent
      };
    });

    // Calculate portfolio totals
    const totalValue = holdingsWithQuotes.reduce((sum, h) => sum + h.currentValue, 0);
    const totalCost = holdingsWithQuotes.reduce((sum, h) => sum + (h.shares * h.purchase_price), 0);
    const totalGainLoss = totalValue - totalCost;
    const totalGainLossPercent = totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0;
    const dayChange = holdingsWithQuotes.reduce((sum, h) => sum + h.dayChange, 0);
    const dayChangePercent = totalValue > 0 ? (dayChange / (totalValue - dayChange)) * 100 : 0;

    return {
      ...portfolio,
      holdings: holdingsWithQuotes,
      totalValue,
      totalCost,
      totalGainLoss,
      totalGainLossPercent,
      dayChange,
      dayChangePercent
    };
  }

  /**
   * Create portfolio with empty quotes for non-quote requests
   */
  private static createPortfolioWithEmptyQuotes(portfolio: Portfolio): PortfolioWithQuotes {
    const holdingsWithQuotes: HoldingWithQuote[] = portfolio.holdings.map(holding => ({
      ...holding,
      currentPrice: 0,
      currentValue: 0,
      totalGainLoss: 0,
      totalGainLossPercent: 0,
      dayChange: 0,
      dayChangePercent: 0
    }));

    return {
      ...portfolio,
      holdings: holdingsWithQuotes,
      totalValue: 0,
      totalCost: 0,
      totalGainLoss: 0,
      totalGainLossPercent: 0,
      dayChange: 0,
      dayChangePercent: 0
    };
  }

  /**
   * Validate a single holding
   */
  private static validateHolding(holding: PortfolioHolding): void {
    if (!holding.ticker || !/^[A-Z]{1,5}$/.test(holding.ticker)) {
      throw new Error('Invalid ticker symbol');
    }
    if (!holding.name || typeof holding.name !== 'string') {
      throw new Error('Company name is required');
    }
    if (!holding.shares || holding.shares <= 0) {
      throw new Error('Shares must be greater than 0');
    }
    if (!holding.purchase_price || holding.purchase_price <= 0) {
      throw new Error('Purchase price must be greater than 0');
    }
    if (!holding.purchase_date || isNaN(Date.parse(holding.purchase_date))) {
      throw new Error('Invalid purchase date');
    }
  }

  /**
   * Validate an array of holdings
   */
  private static validateHoldings(holdings: PortfolioHolding[]): void {
    if (holdings.length > 100) {
      throw new Error('Portfolio cannot have more than 100 holdings');
    }
    
    holdings.forEach(holding => this.validateHolding(holding));
    
    // Check for duplicate tickers
    const tickers = holdings.map(h => h.ticker);
    const uniqueTickers = new Set(tickers);
    if (tickers.length !== uniqueTickers.size) {
      throw new Error('Portfolio cannot have duplicate holdings for the same ticker');
    }
  }

  /**
   * Apply optimistic updates to portfolios
   */
  private static applyOptimisticUpdates(portfolios: Portfolio[]): Portfolio[] {
    const result = [...portfolios];
    
    // Apply pending optimistic updates
    this.optimisticUpdates.forEach((update, id) => {
      if (update.pending) {
        const index = result.findIndex(p => p.id === id);
        if (index >= 0) {
          result[index] = { ...result[index], ...update.data };
        } else if (id.startsWith('temp_')) {
          // Add new optimistic portfolio
          result.unshift(update.data);
        }
      }
    });

    // Clean up old optimistic updates (older than 30 seconds)
    const now = Date.now();
    this.optimisticUpdates.forEach((update, id) => {
      if (now - update.timestamp > 30000) {
        this.optimisticUpdates.delete(id);
      }
    });

    return result;
  }

  /**
   * Clear all optimistic updates (useful for testing)
   */
  static clearOptimisticUpdates(): void {
    this.optimisticUpdates.clear();
  }

  /**
   * Subscribe to real-time portfolio changes
   */
  static subscribeToPortfolioChanges(
    userId: string, 
    callback: (payload: any) => void
  ): () => void {
    const channel = supabase
      .channel(`portfolios:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'portfolios',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          // Invalidate cache on changes
          this.cacheService.delete(`portfolios:${userId}`);
          if ((payload.new as any)?.id) {
            this.cacheService.delete(`portfolio:${(payload.new as any).id}`);
          }
          callback(payload);
        }
      )
      .subscribe();

    // Return unsubscribe function
    return () => {
      supabase.removeChannel(channel);
    };
  }
}